import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { CancellationPolicyResponse, Gross, Hotel, Tax } from 'ts/hotel/interfaces/HotelResponse';
import { chargeConditions } from 'services/hotel';
import Skeleton from 'react-loading-skeleton';
import { RoomClass } from 'ts/hotel/interfaces/HotelResponse';
import { getKey } from 'utils/hotel';
import { formatPrice } from 'utils/utils';
import ScreenshotSupport from 'components/shared/ScreenshotSupport';
import MaximizeModal from 'components/shared/MaximizeModal';
import { dateFormat } from 'utils/shared/utils';
import useDraggableModal from 'hooks/useDraggableModal';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { getCxlClass } from 'utils/hotel';
import { createPortal } from 'react-dom';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';

interface CancellationPolicyModalProps {
  onClose: () => void;
  hotel: Hotel;
  rc: RoomClass;
  htuKey?: string;
  isRemark?: boolean;
}

const CancellationPolicyModal: FC<CancellationPolicyModalProps> = ({
  onClose,
  hotel,
  rc,
  ...props
}) => {
  const [t] = useTranslation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const [response, setResponse] = useState<CancellationPolicyResponse>();
  const currency = sessionStorage.getItem(getKey('displayCurrencyHotels', hotelIndex)) || 'usd';
  const { user } = useContext(MainContext);
  const cxl = response?.cancellationPolicy?.cancellationLimitDate || rc.cxl.limitDate;
  const cxlClass = getCxlClass(cxl);

  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const modalOverlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.isRemark) return;
    (async () => {
      const searchToken = sessionStorage.getItem(getKey('searchTokenHotels', hotelIndex))!;
      const bTokens = rc.hotelRooms.map((room) => room.bToken);
      const newResponse = await chargeConditions({
        searchToken,
        roomBTokenList: bTokens,
      });

      setResponse(newResponse);
    })();
  }, []); // eslint-disable-line

  const decodeHtmlEntities = (encodedString: string): string => {
    return encodedString
      .replaceAll('&amp;lt;', '<')
      .replaceAll('&amp;gt;', '>')
      .replaceAll('&#39;', "'");
  };

  return createPortal(
    <div
      ref={modalOverlayRef}
      className="modal-overlay modal-overlay-top modal-maximizable"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={modalRef} className={'modal modal-lg active'} style={modalStyle}>
        <div
          className={'modal-title draggable-modal-handle' + (isDragging ? ' dragging' : '')}
          {...dragHandleProps}
        >
          <h5>
            <div className="title mb-0">
              {t(props.isRemark ? 'mo.remark' : 'hotel.cancellation_policy')}
            </div>
          </h5>
          <div data-no-drag="true">
            <ScreenshotSupport />
            <MaximizeModal modalOverlayRef={modalOverlayRef} />
            <FontAwesomeIcon icon={faTimes} onClick={() => onClose()} />
          </div>
        </div>
        <div className="modal-content pt-10 text-lh" style={{ maxHeight: 'calc(87vh - 200px)' }}>
          <div>
            <div className="mt-10 mb-5 text-bold text-uppercase text-md">{hotel.item.name}</div>
            <div className="text-light mb-5">{hotel.addressInfo.address}</div>
            <Rating value={hotel.item.star} />
          </div>
          {!props.isRemark && (
            <div className={' mb-20 details-block default-shadow ' + cxlClass}>
              <div
                className={'title title-underline title-header-grey text-lg mb-20'}
                style={
                  response && !response?.cancellationPolicy?.cancellationDesc
                    ? { margin: '-30px -20px -20px' }
                    : {}
                }
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-5" />
                {response ? (
                  <span>
                    {new Date(
                      response?.cancellationPolicy?.cancellationLimitDate || rc.cxl.limitDate
                    ) < new Date()
                      ? t('passenger.cancellation_outdate')
                      : t('passenger.cancellation_date')}
                    :{' '}
                    <span className="text-bold">
                      {dateFormat(
                        response.cancellationPolicy?.cancellationLimitDate,
                        'dd-MMM-yyyy'
                      )}
                    </span>
                  </span>
                ) : (
                  <Skeleton width={200} height={16} className="mb-5" />
                )}
              </div>
              {!response &&
                [30, 40, 70, 0, 0, 100, 100, 80, 90, 89, 75].map((val, index) => (
                  <div key={index}>
                    <Skeleton width={val + '%'} height={22} className="mb-5" />
                  </div>
                ))}
              <div
                dangerouslySetInnerHTML={{
                  __html: decodeHtmlEntities(response?.cancellationPolicy?.cancellationDesc || ''),
                }}
              ></div>

              {!!response?.cancellationPolicy?.hotelRooms?.[0]?.roomPriceBreakdown?.length && (
                <Fragment>
                  <div className="text-md text-bold mt-20 mb-10">{t('hotel.price_breakdown')}</div>
                  <table className="table-breakdown">
                    <thead>
                      <tr>
                        <th colSpan={5}>
                          <span>
                            {t('hotel.room')} - {response.cancellationPolicy.hotelRooms[0].desc}
                          </span>
                          {new Date(rc.cxl.limitDate) < new Date() && (
                            <span>
                              <span> |</span>
                              <span className="ml-5 danger-badge"> {t('hotel.non_ref')}</span>
                            </span>
                          )}
                        </th>
                      </tr>
                      <tr>
                        <th></th>
                        <th>{t('total')}</th>
                        {response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.map(
                          (item, index) => (
                            <th key={index}>{dateFormat(item.toDate, 'dd MMM')}</th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t('price')}</td>
                        <td>
                          {formatPrice(
                            response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.reduce(
                              (acc: number, item) => {
                                return acc + (item.price.gross[currency as keyof Gross] as number);
                              },
                              0
                            ),
                            currency
                          )}
                        </td>
                        {response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.map(
                          (item, index) => (
                            <td key={index}>
                              {formatPrice(
                                item.price.gross[currency as keyof Gross] as number,
                                currency
                              )}
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>{t('taxes')}</td>
                        <td>
                          {formatPrice(
                            response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.reduce(
                              (acc: number, item) => {
                                return acc + (item.price.tax[currency as keyof Tax] as number);
                              },
                              0
                            ),
                            currency
                          )}
                        </td>
                        {response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.map(
                          (item, index) => (
                            <td key={index}>
                              {formatPrice(
                                item.price.tax[currency as keyof Tax] as number,
                                currency
                              )}
                            </td>
                          )
                        )}
                      </tr>
                      <tr>
                        <td>{t('total')}</td>
                        <td>
                          {formatPrice(
                            response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.reduce(
                              (acc: number, item) => {
                                return (
                                  acc +
                                  (item.price.tax[currency as keyof Tax] as number) +
                                  (item.price.gross[currency as keyof Gross] as number)
                                );
                              },
                              0
                            ),
                            currency
                          )}
                        </td>
                        {response.cancellationPolicy.hotelRooms[0].roomPriceBreakdown.map(
                          (item, index) => (
                            <td key={index}>
                              {formatPrice(
                                (item.price.tax[currency as keyof Tax] as number) +
                                (item.price.gross[currency as keyof Gross] as number),
                                currency
                              )}
                            </td>
                          )
                        )}
                      </tr>
                    </tbody>
                  </table>
                </Fragment>
              )}
            </div>
          )}

          <div className={"default-shadow details-block " + cxlClass}>
            <div
              className={'title title-underline title-header-grey text-lg mb-20'}
            >
              {rc.desc ? rc.desc + ' - ' : ''}
              {rc.hotelRooms[0].desc ? rc.hotelRooms[0].desc : ''}{' '}
              {rc.board.basis.desc ? rc.board.basis.desc : ''}
            </div>

            <div className="">
              <div className="mr-10">
                {t('search.check_in')}:{' '}
                <span className="text-bold">{dateFormat(rc.checkIn, 'dd-MMM-yyyy')}</span>
              </div>
              <div className="mr-10">
                {t('search.check_out')}:{' '}
                <span className="text-bold">{dateFormat(rc.checkOut, 'dd-MMM-yyyy')}</span>
              </div>
              <div className="mr-10">
                {t('hotel.nights_up')}:{' '}
                <span className="text-bold">
                  [{rc.nights} {t('nights')}]
                </span>
              </div>
            </div>
            {user?.branch?.hotelDisplaySupplierName && (
              <div>
                {t('hotel.supplier')}: <span className="text-bold">{rc.suppDesc}</span>
              </div>
            )}
          </div>

          {(rc.remCondition || rc.remarks) && (
            <div className={"default-shadow details-block " + cxlClass}>
              <div
                className={'title title-underline title-header-grey text-lg mb-20'}
              >
                <div className="text-bold">{t('hotel.remarks')}</div>
              </div>

              <div>
                <div
                  dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(rc.remCondition || '') }}
                ></div>
                <div
                  dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(rc.remarks || '') }}
                ></div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button title={t('close')} className="btn btn-default" onClick={() => onClose()} />
        </div>
      </div>
    </div>
    , document.body);
};

export default withErrorBoundary(CancellationPolicyModal, 'CancellationPolicyModal');
